import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import type {
  Poll,
  FormQuestionShape,
  DateVoteValue,
} from "@core/lib/pollStorage";
import {
  VoteService,
  ResponseNameTakenError,
} from "@core/services/VoteService";
import { voteRowToFormResponse } from "@core/lib/formResults";
import { getDeviceId } from "@core/lib/guestIdentityService";
import { useDraftAutosave } from "@core/hooks/useDraftAutosave";
import { sendVoteConfirmationEmail } from "@core/services/EmailService";
import { shouldShowQuestion } from "@core/lib/conditionalEvaluator";
import { useToast } from "@core/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { logError, ErrorFactory } from "@core/lib/error-handling";
import DateQuestionVote from "./DateQuestionVote";
import { nextRatingValue } from "./ratingKeyboard";
// import "@core/components/polls/multi-step-animations.css"; // TODO: Créer ce fichier ou utiliser animations inline

interface MultiStepFormVoteProps {
  poll: Poll;
}

type AnswerValue =
  | string
  | string[]
  | Record<string, string | string[]>
  | number
  | DateVoteValue;

export default function MultiStepFormVote({ poll }: MultiStepFormVoteProps) {
  const { t } = useTranslation("forms");
  const { toast } = useToast();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({});
  const [respondentName, setRespondentName] = useState("");
  const [respondentEmail, setRespondentEmail] = useState("");
  const [wantsEmailCopy, setWantsEmailCopy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Phase 2 prod-readiness : autosave du brouillon de réponse pour ne pas
  // perdre la saisie sur crash/refresh/fermeture d'onglet avant l'envoi.
  const draftKey = `doo-form-draft:${poll.id}:${getDeviceId()}`;
  const isPollOpen = poll.status !== "closed";
  const draft = useDraftAutosave(
    draftKey,
    { answers, respondentName, respondentEmail, currentStep },
    { enabled: !submitted && isPollOpen },
  );
  const [draftRestored, setDraftRestored] = useState(false);
  const didRestore = useRef(false);

  useEffect(() => {
    if (didRestore.current) return;
    didRestore.current = true;
    if (submitted || !isPollOpen) return;
    const saved = draft.load();
    if (!saved) return;
    const hasContent =
      (saved.answers && Object.keys(saved.answers).length > 0) ||
      !!saved.respondentName ||
      !!saved.respondentEmail;
    if (!hasContent) return;
    if (saved.answers) setAnswers(saved.answers);
    if (saved.respondentName) setRespondentName(saved.respondentName);
    if (saved.respondentEmail) setRespondentEmail(saved.respondentEmail);
    if (typeof saved.currentStep === "number")
      setCurrentStep(saved.currentStep);
    setDraftRestored(true);
  }, [draft, submitted, isPollOpen]);

  const discardDraft = useCallback(() => {
    draft.clear();
    setAnswers({});
    setRespondentName("");
    setRespondentEmail("");
    setCurrentStep(0);
    setDraftRestored(false);
  }, [draft]);

  // Filtrer les questions visibles selon les règles conditionnelles
  const visibleQuestions = useMemo(() => {
    const allQuestions = poll.questions || [];
    const rules = poll.conditionalRules || [];

    // Simplifier les réponses pour l'évaluation conditionnelle
    const simplifiedAnswers: Record<string, string | string[]> = {};
    Object.entries(answers).forEach(([qId, value]) => {
      if (typeof value === "number") {
        simplifiedAnswers[qId] = value.toString();
      } else if (typeof value === "object" && !Array.isArray(value)) {
        // Vérifier si c'est un DateVoteValue
        if (
          value &&
          Array.isArray(value) &&
          value.length > 0 &&
          "date" in value[0]
        ) {
          // DateVoteValue: vérifier si au moins un vote "yes" ou "maybe"
          const hasVote = (value as Array<{ date: string; vote: string }>).some(
            (item) => item.vote === "yes" || item.vote === "maybe",
          );
          simplifiedAnswers[qId] = hasVote ? "answered" : "";
        } else {
          // Matrix: vérifier si au moins une ligne est remplie
          const hasAnswer = Object.values(value).some((v) =>
            Array.isArray(v) ? v.length > 0 : Boolean(v),
          );
          simplifiedAnswers[qId] = hasAnswer ? "answered" : "";
        }
      } else if (
        Array.isArray(value) &&
        value.length > 0 &&
        typeof value[0] === "object" &&
        "date" in value[0]
      ) {
        // C'est un DateVoteValue - vérifier s'il y a des votes "yes" ou "maybe"
        const hasVote = (value as DateVoteValue).some(
          (item) => item.vote === "yes" || item.vote === "maybe",
        );
        simplifiedAnswers[qId] = hasVote ? "answered" : "";
      } else {
        // Pour les types restants, s'assurer que c'est bien string | string[]
        if (typeof value === "string" || Array.isArray(value)) {
          simplifiedAnswers[qId] = value as string | string[];
        } else {
          // Cas par défaut : traiter comme vide
          simplifiedAnswers[qId] = "";
        }
      }
    });

    return allQuestions.filter((q) =>
      shouldShowQuestion(q.id, rules, simplifiedAnswers),
    );
  }, [poll.questions, poll.conditionalRules, answers]);

  // Ajouter une étape finale pour les coordonnées (step = visibleQuestions.length)
  const totalSteps = visibleQuestions.length + 1; // +1 pour l'étape coordonnées
  const isOnCoordinatesStep = currentStep === visibleQuestions.length;
  const currentQuestion = isOnCoordinatesStep
    ? null
    : visibleQuestions[currentStep];
  const progress = ((currentStep + 1) / totalSteps) * 100;
  const isLastQuestion = currentStep === visibleQuestions.length - 1;

  // Vérifier si la question actuelle est répondue
  const isCurrentQuestionAnswered = useMemo(() => {
    if (!currentQuestion) return false;
    const answer = answers[currentQuestion.id];

    if (currentQuestion.required) {
      // Ne pas rejeter la valeur numérique 0 (rating/NPS/matrix) via un test falsy
      if (answer === null || answer === undefined || answer === "")
        return false;

      // Vérifications spécifiques par type
      if (
        (currentQuestion.kind === "text" ||
          currentQuestion.kind === "long-text") &&
        typeof answer === "string"
      ) {
        return answer.trim().length > 0;
      }
      if (currentQuestion.kind === "multiple" && Array.isArray(answer)) {
        return answer.length > 0;
      }
      if (
        currentQuestion.kind === "matrix" &&
        typeof answer === "object" &&
        !Array.isArray(answer)
      ) {
        const matrixAnswer = answer as Record<string, string | string[]>;
        const rows = currentQuestion.matrixRows || [];
        return rows.every((row) => {
          const rowValue = matrixAnswer[row.id];
          if (currentQuestion.matrixType === "multiple") {
            return Array.isArray(rowValue) && rowValue.length > 0;
          }
          return rowValue !== null && rowValue !== undefined && rowValue !== "";
        });
      }
      if (
        (currentQuestion.kind === "rating" || currentQuestion.kind === "nps") &&
        typeof answer === "number"
      ) {
        return true;
      }
      if (currentQuestion.kind === "date") {
        // Vérifier que c'est un DateVoteValue valide avec au moins un vote
        if (!Array.isArray(answer) || answer.length === 0) {
          return false;
        }
        const dateValue = answer as DateVoteValue;
        return dateValue.some((vote) => vote.vote && vote.date);
      }
      return Boolean(answer);
    }

    return true; // Non requis = toujours OK
  }, [currentQuestion, answers]);

  const goNext = useCallback(() => {
    if (currentStep < visibleQuestions.length) {
      setCurrentStep((prev) => prev + 1);
    }
  }, [currentStep, visibleQuestions.length]);

  const goBack = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  // Navigation clavier
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === "Enter" && isCurrentQuestionAnswered && !isLastQuestion) {
        goNext();
      }
      if (
        e.key === "ArrowRight" &&
        isCurrentQuestionAnswered &&
        !isLastQuestion
      ) {
        goNext();
      }
      if (e.key === "ArrowLeft" && currentStep > 0) {
        goBack();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [currentStep, isCurrentQuestionAnswered, isLastQuestion, goNext, goBack]);

  const handleAnswer = (value: AnswerValue) => {
    setAnswers((prev) => ({
      ...prev,
      [currentQuestion.id]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      // Validation email si demandé
      if (wantsEmailCopy && !respondentEmail.trim()) {
        toast({
          title: t("multistep.toast.emailRequiredTitle"),
          description: t("multistep.toast.emailRequiredDesc"),
          variant: "destructive",
        });
        return;
      }
      if (
        wantsEmailCopy &&
        respondentEmail.trim() &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(respondentEmail.trim())
      ) {
        toast({
          title: t("multistep.toast.emailInvalidTitle"),
          description: t("multistep.toast.emailInvalidDesc"),
          variant: "destructive",
        });
        return;
      }

      const responses = Object.entries(answers).map(([questionId, value]) => ({
        questionId,
        value,
      }));
      const email = wantsEmailCopy ? respondentEmail.trim() : undefined;
      // voterName dupliqué dans response_data : la RPC get_poll_responses
      // stripe la colonne respondent_name (PII) mais laisse passer le jsonb —
      // même pattern que les date polls pour afficher le nom sur Résultats.
      const trimmedName = respondentName?.trim();

      // `allowNameTakeover` : si une réponse existe déjà sous ce nom (autre
      // device), on confirme avant d'écraser au lieu de planter (500).
      const doSubmit = async (allowNameTakeover: boolean) => {
        const voteRow = await VoteService.castVote({
          pollId: poll.id,
          respondentName: trimmedName || `guest-${getDeviceId()}`,
          responseType: "form",
          stableFingerprint: getDeviceId(),
          allowNameTakeover,
          responseData: {
            responses,
            ...(email ? { email } : {}),
            ...(trimmedName ? { voterName: trimmedName } : {}),
          },
        });
        const response = voteRowToFormResponse(voteRow);
        if (email) response.respondentEmail = email;

        // Envoyer l'email si demandé
        if (wantsEmailCopy && respondentEmail.trim()) {
          try {
            await sendVoteConfirmationEmail({
              poll,
              response,
              questions: (poll.questions || []) as FormQuestionShape[],
            });
          } catch (emailError) {
            logError(
              ErrorFactory.api(
                "Erreur lors de l'envoi de l'email",
                "Erreur lors de l'envoi de l'email",
              ),
              {
                component: "MultiStepFormVote",
                operation: "sendVoteConfirmationEmail",
              },
            );
            // Ne pas bloquer la soumission si l'email échoue
          }
        }

        draft.clear();
        return true; // Indicate success
      };

      let submissionError: unknown = null;
      try {
        await doSubmit(false);
      } catch (err) {
        if (err instanceof ResponseNameTakenError) {
          if (window.confirm(t("vote.nameTakenConfirm"))) {
            await doSubmit(true);
          } else {
            return; // l'utilisateur annule : on garde la réponse existante
          }
        } else {
          submissionError = err;
        }
      }
      if (submissionError) throw submissionError;
      setSubmitted(true);
    } catch (error) {
      const isForbiddenOverwrite =
        error instanceof Error && error.message.includes("forbidden_overwrite");
      const description = isForbiddenOverwrite
        ? (error as Error).message
        : t("multistep.toast.submitError");
      if (!isForbiddenOverwrite) {
        logError(
          ErrorFactory.storage(
            "Failed to submit multi-step form response",
            description,
          ),
          {
            component: "MultiStepFormVote",
            pollId: poll.id,
            metadata: { originalError: error },
          },
        );
      }
      toast({
        title: t("multistep.toast.errorTitle"),
        description,
        variant: "destructive",
      });
    }
  };

  // Si aucune question visible, afficher un message
  if (visibleQuestions.length === 0) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "var(--theme-bg-main, #F8FAFC)" }}
      >
        <p className="text-gray-500">{t("multistep.empty")}</p>
      </div>
    );
  }

  // Page de confirmation après soumission
  if (submitted) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ backgroundColor: "var(--theme-bg-main, #F8FAFC)" }}
      >
        <div
          className="max-w-md w-full rounded-2xl shadow-2xl p-6 sm:p-8 text-center animate-fadeIn"
          style={{ backgroundColor: "var(--theme-bg-card, #FFFFFF)" }}
        >
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
            {t("multistep.submitted.title")}
          </h2>
          <p className="text-gray-600 mb-6">
            {t("multistep.submitted.subtitle")}
          </p>

          {/* Message d'information pour la bêta */}
          {/* <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2 text-blue-700">
              <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-sm font-medium">Information bêta</span>
            </div>
            <p className="text-sm text-blue-600 mt-1">
              Pour finaliser et partager votre formulaire, après la bêta, vous devrez vous connecter
              ou créer un compte.
            </p>
          </div> */}
          <div className="space-y-3">
            {(() => {
              const visibility = poll.resultsVisibility || "creator-only";
              const canSeeResults =
                visibility === "public" || visibility === "voters";

              return canSeeResults ? (
                <button
                  onClick={() =>
                    navigate(`/poll/${poll.slug || poll.id}/results`)
                  }
                  className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-3 rounded-lg font-medium hover:from-purple-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg"
                  data-testid="multistepformvote-voir-les-rsultats"
                >
                  {t("multistep.viewResults")}
                </button>
              ) : (
                <div className="text-sm text-gray-500 text-center py-2">
                  ℹ️ {t("multistep.resultsNotPublic")}
                </div>
              );
            })()}
            <button
              onClick={() => navigate("/form")}
              className="w-full bg-white text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-all border border-gray-300"
              data-testid="multistepformvote-retour-laccueil"
            >
              {t("nav.backHome")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: "var(--theme-bg-main, #F8FAFC)" }}
    >
      {/* Barre de progression */}
      <div
        className="w-full h-2"
        style={{ backgroundColor: "var(--theme-border, #E2E8F0)" }}
        data-testid="progress-bar"
      >
        <div
          className="bg-gradient-to-r from-purple-600 to-indigo-600 h-2 transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
          data-testid="progress-fill"
        />
      </div>

      {/* Contenu principal */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-6 sm:py-8">
        <div className="w-full max-w-2xl">
          {/* Titre du formulaire */}
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2 text-center">
            {poll.title}
          </h1>
          {poll.description && (
            <p className="text-center text-gray-600 mb-8">{poll.description}</p>
          )}

          {draftRestored && (
            <div
              className="mb-6 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800 flex items-center justify-between gap-3"
              role="status"
              data-testid="draft-restored-banner"
            >
              <span>{t("vote.draftRestored")}</span>
              <button
                type="button"
                onClick={discardDraft}
                className="underline font-medium shrink-0"
                data-testid="draft-discard"
              >
                {t("vote.draftDiscard")}
              </button>
            </div>
          )}

          {/* Indicateur de progression */}
          <div className="text-center mb-6 sm:mb-8">
            {isOnCoordinatesStep ? (
              <>
                <p className="text-sm text-gray-600 font-medium">
                  {t("multistep.coordinatesStep.heading")}
                </p>
                <p className="text-xs text-purple-600 mt-1">
                  {t("multistep.almostThere")}
                </p>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-600 font-medium">
                  {t("multistep.progress.step", {
                    current: currentStep + 1,
                    total: visibleQuestions.length,
                  })}
                </p>
                {isLastQuestion && (
                  <p className="text-xs text-purple-600 mt-1">
                    {t("multistep.lastQuestion")}
                  </p>
                )}
              </>
            )}
          </div>

          {/* Étape coordonnées OU Question */}
          <div
            className="rounded-2xl shadow-xl p-6 sm:p-8 mb-6 sm:mb-8 animate-fadeIn"
            style={{ backgroundColor: "var(--theme-bg-card, #FFFFFF)" }}
          >
            {isOnCoordinatesStep ? (
              <>
                <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">
                  {t("multistep.coordinates.title")}
                </h2>
                <p className="text-sm text-gray-600 mb-6 text-center">
                  {t("multistep.coordinates.subtitle")}
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("multistep.coordinates.nameLabel")}
                    </label>
                    <input
                      type="text"
                      value={respondentName}
                      onChange={(e) => setRespondentName(e.target.value)}
                      placeholder={t("vote.voterName.anonymousPlaceholder")}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white text-gray-900 placeholder:text-gray-400"
                    />
                  </div>
                  <div>
                    <label className="flex items-center gap-2 mb-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={wantsEmailCopy}
                        onChange={(e) => setWantsEmailCopy(e.target.checked)}
                        className="cursor-pointer"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        {t("multistep.coordinates.emailCopyCheckbox")}
                      </span>
                    </label>
                    {wantsEmailCopy && (
                      <>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t("multistep.coordinates.emailLabel")}
                        </label>
                        <input
                          type="email"
                          value={respondentEmail}
                          onChange={(e) => setRespondentEmail(e.target.value)}
                          placeholder="votre@email.com"
                          required={wantsEmailCopy}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white text-gray-900 placeholder:text-gray-400"
                        />
                      </>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                  {currentQuestion.title}
                  {currentQuestion.required && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                </h2>

                {/* Rendu selon le type de question */}
                <QuestionRenderer
                  question={currentQuestion}
                  value={answers[currentQuestion.id]}
                  onChange={handleAnswer}
                />
              </>
            )}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between gap-4">
            <button
              onClick={goBack}
              disabled={currentStep === 0}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                currentStep === 0
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-white text-gray-700 hover:bg-gray-100 shadow-md hover:shadow-lg"
              }`}
              data-testid="multistepformvote-button"
            >
              <ArrowLeft className="w-5 h-5" />
              {t("multistep.nav.back")}
            </button>

            {isOnCoordinatesStep ? (
              <button
                onClick={handleSubmit}
                className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all bg-gradient-to-r from-green-600 to-green-600 text-white hover:from-green-700 hover:to-green-700 shadow-md hover:shadow-lg"
                data-testid="multistepformvote-button"
              >
                <Check className="w-5 h-5" />
                {t("multistep.nav.submit")}
              </button>
            ) : !isLastQuestion ? (
              <button
                onClick={goNext}
                disabled={!isCurrentQuestionAnswered}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                  isCurrentQuestionAnswered
                    ? "bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 shadow-md hover:shadow-lg"
                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                }`}
                data-testid="multistepformvote-continuer"
              >
                {t("multistep.nav.continue")}
                <ArrowRight className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={goNext}
                disabled={!isCurrentQuestionAnswered}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                  isCurrentQuestionAnswered
                    ? "bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 shadow-md hover:shadow-lg"
                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                }`}
                data-testid="multistepformvote-suivant-vos-coordonnes"
              >
                {t("multistep.nav.nextCoordinates")}
                <ArrowRight className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Hint clavier */}
          <div className="text-center mt-4 text-xs text-gray-500">
            {isCurrentQuestionAnswered && !isLastQuestion && (
              <p>{t("multistep.keyboard.continueHint")}</p>
            )}
            {currentStep > 0 && (
              <p className="mt-1">{t("multistep.keyboard.backHint")}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Composant pour rendre chaque type de question
function QuestionRenderer({
  question,
  value,
  onChange,
}: {
  question: FormQuestionShape;
  value: AnswerValue;
  onChange: (value: AnswerValue) => void;
}) {
  const { t } = useTranslation("forms");
  const kind = question.kind || question.type || "text";
  // Roving tabindex pour les échelles rating/nps (a11y WCAG 2.1.1).
  const scaleRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Single choice
  if (kind === "single") {
    const options = question.options || [];
    return (
      <div className="space-y-3">
        {options.map((option) => (
          <button
            key={option.id}
            onClick={() => onChange(option.id)}
            className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
              value === option.id
                ? "border-purple-600 bg-purple-50 shadow-md"
                : "border-gray-200 hover:border-purple-300 hover:bg-gray-50"
            }`}
            data-testid="multistepformvote-button"
          >
            <div className="flex items-center gap-3">
              <div
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  value === option.id ? "border-purple-600" : "border-gray-300"
                }`}
              >
                {value === option.id && (
                  <div className="w-3 h-3 rounded-full bg-purple-600" />
                )}
              </div>
              <span className="font-medium text-gray-900">{option.label}</span>
            </div>
          </button>
        ))}
      </div>
    );
  }

  // Multiple choice
  if (kind === "multiple") {
    const options = question.options || [];
    const selectedValues = (value as string[]) || [];

    const toggleOption = (optionId: string) => {
      const newValues = selectedValues.includes(optionId)
        ? selectedValues.filter((id) => id !== optionId)
        : [...selectedValues, optionId];
      onChange(newValues);
    };

    return (
      <div className="space-y-3">
        {options.map((option) => (
          <button
            key={option.id}
            onClick={() => toggleOption(option.id)}
            className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
              selectedValues.includes(option.id)
                ? "border-purple-600 bg-purple-50 shadow-md"
                : "border-gray-200 hover:border-purple-300 hover:bg-gray-50"
            }`}
            data-testid="multistepformvote-button"
          >
            <div className="flex items-center gap-3">
              <div
                className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                  selectedValues.includes(option.id)
                    ? "border-purple-600 bg-purple-600"
                    : "border-gray-300"
                }`}
              >
                {selectedValues.includes(option.id) && (
                  <Check className="w-4 h-4 text-white" />
                )}
              </div>
              <span className="font-medium text-gray-900">{option.label}</span>
            </div>
          </button>
        ))}
      </div>
    );
  }

  // Text
  if (kind === "text") {
    return (
      <input
        type="text"
        value={(value as string) || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={question.placeholder || t("vote.textPlaceholder")}
        maxLength={question.maxLength}
        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white text-gray-900 placeholder:text-gray-400"
      />
    );
  }

  // Long Text
  if (kind === "long-text") {
    return (
      <textarea
        value={(value as string) || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={question.placeholder || t("vote.longTextPlaceholder")}
        maxLength={question.maxLength}
        rows={6}
        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-y bg-white text-gray-900 placeholder:text-gray-400"
      />
    );
  }

  // Rating
  if (kind === "rating") {
    const scale = question.ratingScale || 5;
    const currentValue = (value as number) || 0;
    const ratingVal = typeof value === "number" ? value : null;
    const activeIdx = ratingVal ? Math.max(0, ratingVal - 1) : 0;

    return (
      <div className="space-y-4">
        <div
          className="flex justify-between gap-2"
          role="radiogroup"
          aria-label={t("rating.aria.group", { scale })}
          tabIndex={-1}
          onKeyDown={(e) => {
            const next = nextRatingValue(e.key, ratingVal, 1, scale);
            if (next === null) return;
            e.preventDefault();
            onChange(next);
            scaleRefs.current[next - 1]?.focus();
          }}
        >
          {Array.from({ length: scale }, (_, i) => i + 1).map((num, idx) => (
            <button
              key={num}
              onClick={() => onChange(num)}
              role="radio"
              aria-checked={currentValue === num}
              aria-label={t("rating.aria.option", { num, scale })}
              tabIndex={idx === activeIdx ? 0 : -1}
              ref={(el) => {
                scaleRefs.current[idx] = el;
              }}
              className={`flex-1 py-4 min-h-[44px] rounded-lg border-2 font-bold text-lg transition-all ${
                currentValue === num
                  ? "border-purple-600 bg-purple-600 text-white shadow-lg scale-110"
                  : "border-gray-200 hover:border-purple-300 hover:bg-gray-50 text-gray-900"
              }`}
              data-testid="multistepformvote-button"
            >
              {num}
            </button>
          ))}
        </div>
        {(question.ratingMinLabel || question.ratingMaxLabel) && (
          <div className="flex justify-between text-xs text-gray-700">
            <span>{question.ratingMinLabel || ""}</span>
            <span>{question.ratingMaxLabel || ""}</span>
          </div>
        )}
      </div>
    );
  }

  // NPS
  if (kind === "nps") {
    const currentValue = (value as number) || -1;
    const npsVal = typeof value === "number" ? value : null;
    const activeIdx = npsVal ?? 0;

    return (
      <div className="space-y-4">
        <div className="overflow-x-auto">
          <div
            className="grid grid-cols-11 gap-1 min-w-[484px]"
            role="radiogroup"
            aria-label={t("nps.aria.group")}
            tabIndex={-1}
            onKeyDown={(e) => {
              const next = nextRatingValue(e.key, npsVal, 0, 10);
              if (next === null) return;
              e.preventDefault();
              onChange(next);
              scaleRefs.current[next]?.focus();
            }}
          >
            {Array.from({ length: 11 }, (_, i) => i).map((num) => (
              <button
                key={num}
                onClick={() => onChange(num)}
                role="radio"
                aria-checked={currentValue === num}
                aria-label={t("nps.aria.option", { num })}
                tabIndex={num === activeIdx ? 0 : -1}
                ref={(el) => {
                  scaleRefs.current[num] = el;
                }}
                className={`py-3 min-h-[44px] min-w-[44px] rounded-lg border-2 font-bold text-sm transition-all ${
                  currentValue === num
                    ? "border-purple-600 bg-purple-600 text-white shadow-lg scale-110"
                    : "border-gray-200 hover:border-purple-300 hover:bg-gray-50 text-gray-900"
                }`}
                data-testid="multistepformvote-button"
              >
                {num}
              </button>
            ))}
          </div>
        </div>
        <div className="flex justify-between text-xs text-gray-700">
          <span>{t("nps.scale.notLikely")}</span>
          <span>{t("nps.scale.extremelyLikely")}</span>
        </div>
      </div>
    );
  }

  // Matrix
  if (kind === "matrix") {
    const rows = question.matrixRows || [];
    const columns = question.matrixColumns || [];
    const matrixValue = (value as Record<string, string | string[]>) || {};

    const handleMatrixChange = (rowId: string, colId: string) => {
      const newValue = { ...matrixValue };

      if (question.matrixType === "multiple") {
        const current = (newValue[rowId] as string[]) || [];
        newValue[rowId] = current.includes(colId)
          ? current.filter((id) => id !== colId)
          : [...current, colId];
      } else {
        newValue[rowId] = colId;
      }

      onChange(newValue);
    };

    return (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="p-2 text-left border-b-2 border-gray-300 bg-gray-50"></th>
              {columns.map((col) => (
                <th
                  key={col.id}
                  className="p-2 text-center border-b-2 border-gray-300 bg-gray-50 text-sm font-medium text-gray-800"
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                className="border-b border-gray-200 hover:bg-gray-50"
              >
                <td className="p-3 font-medium text-sm text-gray-800 bg-white">
                  {row.label}
                </td>
                {columns.map((col) => {
                  const isSelected =
                    question.matrixType === "multiple"
                      ? ((matrixValue[row.id] as string[]) || []).includes(
                          col.id,
                        )
                      : matrixValue[row.id] === col.id;

                  return (
                    <td
                      key={col.id}
                      className="p-3 text-center bg-white border-r border-gray-200"
                    >
                      <button
                        onClick={() => handleMatrixChange(row.id, col.id)}
                        className={`w-8 h-8 rounded-full border-2 transition-all ${
                          isSelected
                            ? "border-purple-600 bg-purple-600"
                            : "border-gray-400 hover:border-purple-400 hover:bg-gray-50"
                        }`}
                        data-testid="multistepformvote-button"
                      >
                        {isSelected && (
                          <Check className="w-4 h-4 text-white mx-auto" />
                        )}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Date
  if (kind === "date") {
    return (
      <DateQuestionVote
        question={question}
        value={Array.isArray(value) ? (value as DateVoteValue) : undefined}
        onChange={(newVal) => onChange(newVal)}
        required={question.required}
      />
    );
  }

  return (
    <p className="text-gray-500">{t("multistep.unsupportedQuestionType")}</p>
  );
}
